import {
  Box,
  ActionListItem,
  ModalHeader,
  ActionList,
  Dropdown,
  DropdownOverlay,
  ModalBody,
  Modal,
  SelectInput,
  TextInput,
  DatePicker,
  TextArea,
  FileUpload,
  Button,
  ModalFooter,
  Heading,
  Spinner,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { MimeType } from 'components/ui/FileUpload/constants';
import type { BladeFile, BladeFileList } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { CreateClaimInitialState, maxProofFilesCount, maxProofFilesSize } from './constant';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { findFileById, getBase64, updateFileStatus } from './util';
import { PDFViewer } from 'components/ui/PDFViewer';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import api from 'api';
import { uploadClaimEvidenceFiles } from 'api/gql-utils/flexibleBenefits/mutations/uploadClaimEvidenceFiles';
import {
  FlexibleBenefitAssignedComponent,
  FlexiReimbursementCreateClaimInput,
  FlexiReimbursementCreateClaimResponse,
  FlexiReimbursementEditClaimInput,
} from './types';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useSelector } from 'react-redux';
import { convertRupeesToPaise } from '../Edit/util';
import { FlexibleBenefitsComponent } from '../../types';
import { useSearchParams } from 'hooks/useSearchParams';

export const CreateClaimModal = ({ isEditFlow = false }: { isEditFlow?: boolean }) => {
  const { formData, setFieldValue, errors, setFieldError, clearFieldError } =
    useFormWithProvider<typeof CreateClaimInitialState>();

  const isRejectedClaim = formData.Claim_status === 'REJECTED';

  const filesRef = React.useRef<BladeFile[]>(formData.Files || []);

  useEffect(() => {
    filesRef.current = formData.Files || [];
  }, [formData.Files]);

  const { urlParams } = useSearchParams();

  const activeUserId = useSelector(loggedInUserSelectors.peopleId);
  const userId = urlParams.get('userId') || activeUserId;

  const {
    data: flexibleBenefitAssignedComponentsData,
    isLoading: isLoadingFlexibleBenefitAssignedComponents,
  } = useQuery({
    queryKey: ['payrollListAssignedComponents', userId],
    queryFn: () => {
      return api.flexibleBenefitComponents.queries.getFlexibleBenefitAssignedComponents({
        user_id: userId?.toString() || '',
        limit: 100,
        offset: 0,
      });
    },
    select: (data) => {
      return data.payrollListAssignedComponents.data;
    },
    enabled: !!userId,
  });

  const { data: flexibleBenefitsListData, isLoading: isLoadingFlexibleBenefitsList } = useQuery({
    queryKey: ['flexibleComponentListData', userId],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefitsList({
        offset: 0,
        limit: 100,
      }),
    select: (data) => data.payrollListFlexibleBenefitsComponents.data,
    onError: (error) => {
      toast.show({
        content: 'Failed to load flexible components list, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
    enabled: !!userId,
  });

  const assignedCashBenefits = useMemo(() => {
    return flexibleBenefitAssignedComponentsData?.filter(
      (component: FlexibleBenefitAssignedComponent) => {
        return (
          flexibleBenefitsListData?.find(
            (benefit: FlexibleBenefitsComponent) => benefit.id === component.component_id,
          )?.settings.flexible_settings?.salary_settings?.non_cash_benefit === false
        );
      },
    );
  }, [flexibleBenefitsListData, flexibleBenefitAssignedComponentsData]);

  const toast = useToast();

  const ufhClaimUpload = useMutation({
    mutationKey: [uploadClaimEvidenceFiles],
    mutationFn: (file: {
      file_name: string;
      content_type: string;
      file_content: string;
      id: string;
    }) => {
      return uploadClaimEvidenceFiles({
        filename: file.file_name || '',
        content_type: file.content_type,
        file_content: file.file_content.replace(/^data:[^;]+;base64,/, ''),
        id: file.id,
      });
    },
    onSuccess: (data) => {
      if (!data.payrollUploadClaimEvidence.file_id) {
        toast.show({
          content: 'Something went wrong while uploading the file',
          color: 'negative',
          duration: 3000,
        });
        return;
      }
      const updatedFileList = updateFileStatus(
        filesRef.current,
        data.payrollUploadClaimEvidence.id || '',
        data.payrollUploadClaimEvidence.file_id || '',
        'success',
      );
      const uploadedFileName = updatedFileList.find(
        (f: BladeFile) => f.id === data.payrollUploadClaimEvidence.file_id,
      )?.name;

      setFieldValue('Files', updatedFileList);
      toast.show({
        content: `File ${uploadedFileName} uploaded successfully`,
        color: 'positive',
        duration: 3000,
      });
    },
    onError: (error: Error & { id: string }) => {
      const updatedFileList = updateFileStatus(filesRef.current, error.id || '', '', 'error');
      setFieldValue('Files', updatedFileList);
      toast.show({
        content: error?.message || 'Something went wrong while uploading the file',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const createClaim = useMutation<
    FlexiReimbursementCreateClaimResponse,
    Error,
    FlexiReimbursementCreateClaimInput
  >({
    mutationKey: [api.flexibleBenefitComponents.mutations.createFlexiReimbursementClaim],
    mutationFn: (data) => {
      return api.flexibleBenefitComponents.mutations.createFlexiReimbursementClaim(data);
    },
    onSuccess: () => {
      toast.show({
        content: 'Claim created successfully',
        color: 'positive',
        duration: 3000,
      });
      history.goBack();
    },
    onError: (error) => {
      toast.show({
        content: error?.message || 'Something went wrong while creating the claim',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const editClaim = useMutation<
    FlexiReimbursementCreateClaimResponse,
    Error,
    FlexiReimbursementEditClaimInput
  >({
    mutationKey: [api.flexibleBenefitComponents.mutations.editFlexiReimbursementClaim],
    mutationFn: (data) => {
      return api.flexibleBenefitComponents.mutations.editFlexiReimbursementClaim(data);
    },
    onSuccess: () => {
      toast.show({
        content: 'Claim updated successfully',
        color: 'positive',
        duration: 3000,
      });
      history.goBack();
    },
    onError: (error) => {
      toast.show({
        content: error?.message || 'Something went wrong while updating the claim',
        color: 'negative',
        duration: 3000,
      });
    },
  });
  const handleBenefitTypeChange = ({ values }: { values: string[] }) => {
    clearFieldError('Component_name');
    setFieldValue('Component_name', values[0] || '');
    setFieldValue('Component_id', values[1] || '');
  };

  const handleBillAmountChange = ({ value }: { value?: string }) => {
    clearFieldError('Bill_amount');
    setFieldValue('Bill_amount', value || '');
  };

  const handleExpenseDateChange = (value: Date | null) => {
    clearFieldError('Expense_date');
    setFieldValue('Expense_date', value || null);
  };

  const handleDescriptionChange = ({ value }: { value?: string }) => {
    clearFieldError('Description');
    setFieldValue('Description', value || '');
  };

  const handleFileUpload = async ({ fileList }: { fileList: BladeFileList }) => {
    clearFieldError('Files');

    fileList.forEach(async (file) => {
      const base64 = await getBase64(file as File);
      const fileId = `${new Date().getTime()}_${file.name}`;

      // Skip if file upload already started/completed
      if (findFileById(formData.Files, file.id || '')) {
        return;
      }
      file.status = 'uploading';
      file.id = fileId;
      setFieldValue('Files', [...(filesRef.current || []), file]);
      ufhClaimUpload.mutate({
        file_name: file.name,
        content_type: file.type,
        file_content: base64 as string,
        id: fileId,
      });
    });
  };

  const handleFileRemove = (file: { file: BladeFile }) => {
    const fileList =
      filesRef.current?.filter((f: BladeFile) => {
        return f.id !== file.file.id;
      }) || [];

    setFieldValue('Files', fileList);
  };

  const expenseDate = formData.Expense_date ? new Date(Number(formData.Expense_date)) : null;

  const validateForm = () => {
    let isValid = true;
    const isValidNumber = (val: string) => /^\d+$/.test(val);

    if (!formData.Component_name) {
      setFieldError('Component_name', 'Benefit type is required');
      isValid = false;
    }
    if (
      !formData.Bill_amount ||
      !isValidNumber(formData.Bill_amount) ||
      Number(formData.Bill_amount) <= 0
    ) {
      setFieldError('Bill_amount', 'Please enter a valid bill amount');
      isValid = false;
    }
    if (!formData.Expense_date) {
      setFieldError('Expense_date', 'Expense date is required');
      isValid = false;
    }
    if (formData?.Files?.length === 0) {
      setFieldError('Files', 'Proof documents are required');
      isValid = false;
    }
    return isValid;
  };

  const onSubmit = () => {
    const isValid = validateForm();

    if (isValid) {
      if (isEditFlow && !isRejectedClaim) {
        editClaim.mutate({
          claim_id: formData.Claim_id || '',
          component_id: formData.Component_id || '',
          bill_amount: convertRupeesToPaise(formData.Bill_amount || '0') || 0,
          description: formData.Description || '',
          files: formData.Files?.map((f: BladeFile) => f.id || '') || [],
          expense_date: expenseDate ? Math.floor(expenseDate.getTime() / 1000).toString() : '',
          user_id: userId?.toString() || '',
        });
      } else {
        createClaim.mutate({
          component_id: formData.Component_id || '',
          bill_amount: convertRupeesToPaise(formData.Bill_amount || '0') || 0,
          description: formData.Description || '',
          files: formData.Files?.map((f: BladeFile) => f.id || '') || [],
          expense_date: expenseDate ? Math.floor(expenseDate.getTime() / 1000).toString() : '',
          user_id: userId?.toString() || '',
        });
      }
    }
  };

  const [previewFile, setPreviewFile] = useState<BladeFile | null>(null);

  const handleFilePreviewClick = (file: { file: BladeFile }) => {
    setPreviewFile(file.file);
  };

  const onPreviewClose = () => {
    setPreviewFile(null);
  };

  const handleFileDismiss = ({ file }: { file: File }) => {
    setFieldValue(
      'Files',
      formData.Files?.filter((f: BladeFile) => f.id !== (file as BladeFile).id),
    );
  };

  const history = useHistory();

  return (
    <>
      <Modal
        size="full"
        isOpen={true}
        onDismiss={() => {
          history.push({
            pathname: routePaths.flexibleBenefits.claims.listView,
            search: `userId=${userId?.toString() || ''}`,
          });
        }}>
        <ModalHeader title={`${isEditFlow && !isRejectedClaim ? 'Modify' : 'Create New'} Claim`} />
        <ModalBody>
          {isLoadingFlexibleBenefitAssignedComponents || isLoadingFlexibleBenefitsList ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height={'calc(100vh - 76px)'}
              gap="spacing.3">
              <Spinner size="medium" accessibilityLabel="Loading" />
              <Text>Loading...</Text>
            </Box>
          ) : (
            <Box width="100%" display="flex" justifyContent="center" height={'calc(100vh - 76px)'}>
              <Box width="704px" display="flex" flexDirection="column" gap="spacing.9">
                <Box display="flex" flexDirection="row" gap="spacing.2" alignItems="center">
                  <Heading size="large" weight="semibold" color="surface.text.gray.subtle">
                    {isEditFlow && !isRejectedClaim ? 'Edit' : 'New'} Claim
                  </Heading>
                  <Heading size="large" weight="regular" color="surface.text.gray.muted">
                    Upload
                  </Heading>
                </Box>

                <Box display="flex" flexDirection="column" gap="spacing.7">
                  <Dropdown selectionType="single">
                    <SelectInput
                      label="Benefit type"
                      placeholder="Select benefit"
                      value={formData.Component_name}
                      defaultValue={formData.Component_name}
                      labelPosition="left"
                      necessityIndicator="required"
                      size="medium"
                      validationState={errors.Component_name ? 'error' : 'none'}
                      errorText={errors.Component_name}
                    />
                    <DropdownOverlay>
                      <ActionList>
                        {(assignedCashBenefits || [])?.map(
                          (component: FlexibleBenefitAssignedComponent) => (
                            <ActionListItem
                              key={component.assignment_id}
                              title={component.component_display_name}
                              value={component.component_display_name}
                              onClick={() => {
                                handleBenefitTypeChange({
                                  values: [
                                    component.component_display_name,
                                    component.component_id,
                                  ],
                                });
                              }}
                            />
                          ),
                        )}
                      </ActionList>
                    </DropdownOverlay>
                  </Dropdown>

                  <TextInput
                    label="Bill Amount"
                    placeholder="Enter bill amount (e.g., 5000)"
                    value={formData.Bill_amount}
                    onChange={handleBillAmountChange}
                    labelPosition="left"
                    necessityIndicator="required"
                    size="medium"
                    prefix="₹"
                    type="number"
                    validationState={errors.Bill_amount ? 'error' : 'none'}
                    errorText={errors.Bill_amount}
                    helpText="Enter the amount mentioned on the bill. If uploading multiple bills, enter the total amount for all bills"
                  />

                  <DatePicker
                    selectionType="single"
                    label="Expense date"
                    defaultValue={expenseDate}
                    onChange={handleExpenseDateChange}
                    labelPosition="left"
                    necessityIndicator="required"
                    size="medium"
                    validationState={errors.Expense_date ? 'error' : 'none'}
                    errorText={errors.Expense_date}
                    helpText="Enter the date of the expense as shown in the proof document you are going to upload"
                    maxDate={new Date()}
                  />

                  <TextArea
                    label="Description"
                    placeholder="Add any additional details about your claim"
                    value={formData.Description}
                    onChange={handleDescriptionChange}
                    labelPosition="left"
                    size="medium"
                  />

                  <FileUpload
                    uploadType="multiple"
                    label="Upload Proof"
                    labelPosition="left"
                    necessityIndicator="required"
                    size="medium"
                    helpText="You can upload upto 5 files. Max size 4MB."
                    maxCount={maxProofFilesCount}
                    maxSize={maxProofFilesSize}
                    onChange={handleFileUpload}
                    fileList={formData.Files}
                    validationState={errors.Files ? 'error' : 'none'}
                    errorText={errors.Files}
                    onRemove={handleFileRemove}
                    accept={[MimeType.PDF, MimeType.JPEG, MimeType.PNG].join(',')}
                    onPreview={handleFilePreviewClick}
                    onDismiss={handleFileDismiss}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Box display="flex" height={'fit-content'} justifyContent="flex-end" width="100%">
            <Button
              size="medium"
              onClick={onSubmit}
              isDisabled={ufhClaimUpload.isLoading}
              isLoading={createClaim.isLoading || editClaim.isLoading}>
              Send for approval
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
      <PDFViewer
        isOpen={!!previewFile}
        files={previewFile ? [previewFile] : []}
        onClose={onPreviewClose}
      />
    </>
  );
};
